import {
  Ship, Anchor, Globe2, Warehouse, Truck, PackageCheck, Boxes, ShieldCheck,
  Wheat, Factory, ShoppingBasket, Building2, Compass, BadgeCheck, Award
} from "lucide-react";

export const ASSETS = {
  logoFull: "/assets/ssa-logo-full.png",
  emblem: "/assets/ssa-emblem.png",
};

export const IMAGES = {
  heroPrimary: "https://images.pexels.com/photos/14392678/pexels-photo-14392678.jpeg?auto=compress&cs=tinysrgb&w=1920",
  heroAlt: "https://images.pexels.com/photos/10347618/pexels-photo-10347618.jpeg?auto=compress&cs=tinysrgb&w=1920",
  portNight: "https://images.pexels.com/photos/21728446/pexels-photo-21728446.jpeg?auto=compress&cs=tinysrgb&w=1600",
  vesselDock: "https://images.pexels.com/photos/32399138/pexels-photo-32399138.jpeg?auto=compress&cs=tinysrgb&w=1600",
  containers: "https://images.pexels.com/photos/1117210/pexels-photo-1117210.jpeg?auto=compress&cs=tinysrgb&w=1600",
  cranes: "https://images.pexels.com/photos/906982/pexels-photo-906982.jpeg?auto=compress&cs=tinysrgb&w=1600",
  aerialPort: "https://images.pexels.com/photos/1554646/pexels-photo-1554646.jpeg?auto=compress&cs=tinysrgb&w=1600",
  u_ship1: "https://images.unsplash.com/photo-1605745341112-85968b19335b?w=1600&q=80",
  u_ship2: "https://images.unsplash.com/photo-1494412574643-ff11b0a5c1c3?w=1600&q=80",
  u_port1: "https://images.unsplash.com/photo-1517999144091-3d9dca6d1e43?w=1600&q=80",
  u_warehouse: "https://images.unsplash.com/photo-1578575437130-527eed3abbec?w=1600&q=80",
  u_truck: "https://images.unsplash.com/photo-1601584115197-04ecc0da31d7?w=1600&q=80",
  u_crane2: "https://images.unsplash.com/photo-1559825481-12a05cc00344?w=1600&q=80",
  u_containers2: "https://images.unsplash.com/photo-1518306727298-4c17e1bf6942?w=1600&q=80",
  u_ocean: "https://images.unsplash.com/photo-1473445730015-841f29a9490b?w=1600&q=80",
  u_cargo: "https://images.unsplash.com/photo-1545459720-aac8509eb02c?w=1600&q=80",
};

export const NAV_LINKS = [
  { label: "Home", to: "/" },
  { label: "About Us", to: "/about" },
  { label: "Services", to: "/services" },
  { label: "Global Network", to: "/global-network" },
  { label: "Industries", to: "/industries" },
  { label: "Contact", to: "/contact" },
];

export const HERO_STATS = [
  { value: 2002, suffix: "", label: "Established", prefix: "", plain: true },
  { value: 40, suffix: "+", label: "International Agent Partners", prefix: "" },
  { value: 8, suffix: "", label: "Specialised Service Divisions", prefix: "" },
  { value: 6, suffix: "", label: "Global Trade Regions", prefix: "" },
];

export const TIMELINE = [
  {
    year: "2002",
    title: "Company Established",
    text: "Speed Shipping Agencies is founded in Kolkata, laying the foundation for a specialist maritime logistics and freight forwarding practice rooted in integrity and operational discipline.",
  },
  {
    year: "2008",
    title: "Regional Expansion",
    text: "Operations expand across India's principal gateway ports and inland corridors, broadening customs clearing, warehousing and multimodal transportation capabilities.",
  },
  {
    year: "2015",
    title: "International Network Growth",
    text: "A curated network of international agents is consolidated across Asia, the Middle East, Africa and Europe — enabling seamless door-to-door movement across global trade lanes.",
  },
  {
    year: "2025",
    title: "Advanced Logistics Operations",
    text: "Integrated supply chain solutions, vessel chartering and project cargo expertise position SSA as a trusted partner for complex, high-value maritime logistics.",
  },
];

export const SERVICES = [
  {
    slug: "customs-clearing",
    name: "Customs Clearing",
    icon: ShieldCheck,
    image: IMAGES.u_port1,
    short: "Licensed customs brokerage ensuring compliant, expedited cargo release at every gateway.",
    highlights: ["Documentation & compliance", "Duty optimisation", "Faster cargo release"],
    overview: "Our licensed customs clearing division manages the full lifecycle of import and export documentation, classification and regulatory compliance. With deep familiarity across customs regimes and port authorities, we ensure cargo moves through borders with precision, transparency and minimal dwell time.",
    process: [
      { step: "Documentation Review", text: "Verification of commercial invoices, packing lists, certificates of origin and statutory permits." },
      { step: "Classification & Valuation", text: "Accurate HS code classification and duty assessment to optimise landed cost." },
      { step: "Customs Filing", text: "Electronic filing and liaison with customs authorities for assessment and examination." },
      { step: "Release & Handover", text: "Duty settlement, cargo release and coordinated onward delivery." },
    ],
    applications: ["Import / Export", "Bonded Cargo", "Re-export", "Temporary Admission"],
    benefits: [
      { title: "Compliance Assurance", text: "Rigorous adherence to statutory and regulatory frameworks across jurisdictions." },
      { title: "Reduced Dwell Time", text: "Proactive filing and authority liaison to minimise port and terminal delays." },
      { title: "Cost Efficiency", text: "Strategic classification and duty planning to protect margins." },
    ],
  },
  {
    slug: "freight-forwarding",
    name: "Freight Forwarding",
    icon: Globe2,
    image: IMAGES.heroAlt,
    short: "End-to-end ocean and air freight forwarding across international trade corridors.",
    highlights: ["FCL & LCL ocean freight", "Air freight solutions", "Door-to-door coordination"],
    overview: "As a full-service freight forwarder, SSA orchestrates the movement of cargo across ocean and air networks. Leveraging strong carrier relationships and a global agent network, we design reliable, cost-effective routings tailored to each shipment's commercial and time sensitivities.",
    process: [
      { step: "Requirement Assessment", text: "Understanding cargo profile, incoterms, timelines and destination requirements." },
      { step: "Routing & Carrier Selection", text: "Optimal mode, carrier and lane selection balancing cost, transit and reliability." },
      { step: "Booking & Documentation", text: "Space booking, bill of lading and shipping instructions management." },
      { step: "Tracking & Delivery", text: "Milestone visibility through to final destination handover." },
    ],
    applications: ["FCL", "LCL / Consolidation", "Air Freight", "Multimodal"],
    benefits: [
      { title: "Global Reach", text: "Access to international lanes through a trusted agent network." },
      { title: "Optimised Routings", text: "Cost and transit-time efficiency through carefully engineered routings." },
      { title: "Single Point of Control", text: "One accountable partner across the entire shipment lifecycle." },
    ],
  },
  {
    slug: "warehouse-distribution",
    name: "Warehouse & Distribution",
    icon: Warehouse,
    image: IMAGES.u_warehouse,
    short: "Secure warehousing and distribution with disciplined inventory governance.",
    highlights: ["Bonded & general storage", "Inventory management", "Order fulfilment"],
    overview: "Our warehousing and distribution services provide secure, well-governed storage and onward fulfilment. From bonded facilities to managed inventory and distribution, we extend the supply chain with reliability and full traceability.",
    process: [
      { step: "Inbound & Receiving", text: "Goods receipt, inspection and systematic put-away." },
      { step: "Storage & Control", text: "Climate-appropriate storage with continuous inventory governance." },
      { step: "Order Management", text: "Pick, pack and dispatch aligned to client SLAs." },
      { step: "Distribution", text: "Coordinated last-mile and regional distribution." },
    ],
    applications: ["Bonded Storage", "Distribution Centres", "Inventory Pooling", "Cross-dock"],
    benefits: [
      { title: "Inventory Visibility", text: "Accurate, real-time stock governance and reporting." },
      { title: "Scalable Capacity", text: "Flexible storage that scales with seasonal and growth demand." },
      { title: "Secure Handling", text: "Controlled, monitored facilities protecting cargo integrity." },
    ],
  },
  {
    slug: "inland-transportation",
    name: "Inland Transportation",
    icon: Truck,
    image: IMAGES.u_truck,
    short: "Reliable inland haulage and multimodal connectivity between ports and destinations.",
    highlights: ["Container haulage", "Multimodal linkage", "Nationwide coverage"],
    overview: "SSA delivers dependable inland transportation, connecting maritime gateways with inland origins and destinations. Our managed haulage and multimodal solutions ensure cargo continuity across the first and last mile.",
    process: [
      { step: "Transport Planning", text: "Route, equipment and schedule planning aligned to vessel windows." },
      { step: "Equipment Allocation", text: "Right-sized fleet and container equipment deployment." },
      { step: "Execution & Tracking", text: "Monitored movement with milestone updates." },
      { step: "Delivery Confirmation", text: "Proof of delivery and documentation closure." },
    ],
    applications: ["Port Haulage", "Rail-Road Multimodal", "Project Movements", "Distribution Runs"],
    benefits: [
      { title: "Network Continuity", text: "Seamless linkage between sea, rail and road." },
      { title: "On-Time Reliability", text: "Disciplined scheduling synchronised to port operations." },
      { title: "Nationwide Coverage", text: "Extensive inland reach across key corridors." },
    ],
  },
  {
    slug: "pick-and-pack",
    name: "Pick & Pack",
    icon: PackageCheck,
    image: IMAGES.u_containers2,
    short: "Precision pick, pack and value-added services for retail and distribution readiness.",
    highlights: ["Order accuracy", "Kitting & labelling", "Retail-ready packing"],
    overview: "Our pick and pack operations deliver order accuracy and presentation quality. From kitting and labelling to retail-ready packaging, we prepare cargo for onward distribution with meticulous attention to detail.",
    process: [
      { step: "Order Receipt", text: "Validation of order specifications and packing instructions." },
      { step: "Picking", text: "Accurate item selection with verification controls." },
      { step: "Packing & VAS", text: "Protective packing, kitting, labelling and value-added services." },
      { step: "Dispatch", text: "Quality-checked handover for distribution." },
    ],
    applications: ["E-commerce", "Retail Distribution", "Kitting", "Promotional Packs"],
    benefits: [
      { title: "Order Accuracy", text: "Controlled processes that minimise picking errors." },
      { title: "Brand Presentation", text: "Retail-ready packaging that protects brand standards." },
      { title: "Flexible Throughput", text: "Capacity that adapts to demand peaks." },
    ],
  },
  {
    slug: "project-cargo",
    name: "Project Cargo",
    icon: Boxes,
    image: IMAGES.cranes,
    short: "Engineered handling of oversized, heavy-lift and out-of-gauge project shipments.",
    highlights: ["Heavy-lift planning", "Route surveys", "End-to-end project management"],
    overview: "Project cargo demands engineering precision and meticulous coordination. SSA manages oversized, heavy-lift and out-of-gauge consignments — from feasibility and route surveys to lifting plans and on-site delivery.",
    process: [
      { step: "Feasibility & Survey", text: "Technical assessment, route surveys and risk evaluation." },
      { step: "Lifting & Method Plan", text: "Engineered lifting plans and equipment specification." },
      { step: "Execution", text: "Coordinated multimodal movement with permits and escorts." },
      { step: "Site Delivery", text: "Controlled offloading and positioning at destination." },
    ],
    applications: ["Energy & Power", "Infrastructure", "Heavy Machinery", "EPC Projects"],
    benefits: [
      { title: "Engineered Safety", text: "Rigorous method statements protecting cargo and people." },
      { title: "Specialist Equipment", text: "Access to heavy-lift and specialised transport assets." },
      { title: "Total Project Control", text: "Single-point coordination across complex movements." },
    ],
  },
  {
    slug: "vessel-chartering",
    name: "Vessel Chartering",
    icon: Ship,
    image: IMAGES.u_ship1,
    short: "Tailored vessel chartering for bulk, breakbulk and specialised maritime requirements.",
    highlights: ["Voyage & time charter", "Bulk & breakbulk", "Market expertise"],
    overview: "Our chartering desk arranges the right tonnage for every requirement. With strong market intelligence and broker relationships, we negotiate voyage and time charters for bulk, breakbulk and specialised cargoes on competitive terms.",
    process: [
      { step: "Cargo Analysis", text: "Assessment of cargo, stowage and laycan requirements." },
      { step: "Tonnage Sourcing", text: "Identification and vetting of suitable vessels." },
      { step: "Negotiation & Fixture", text: "Charter party negotiation and fixture conclusion." },
      { step: "Operations", text: "Voyage oversight, laytime and performance monitoring." },
    ],
    applications: ["Dry Bulk", "Breakbulk", "Project Tonnage", "Spot & Period"],
    benefits: [
      { title: "Market Intelligence", text: "Informed decisions backed by chartering expertise." },
      { title: "Competitive Terms", text: "Optimised commercial outcomes through skilled negotiation." },
      { title: "Operational Oversight", text: "Diligent voyage management from fixture to discharge." },
    ],
  },
  {
    slug: "marine-insurance",
    name: "Marine Insurance",
    icon: Anchor,
    image: IMAGES.u_ocean,
    short: "Cargo and marine insurance advisory safeguarding value across the voyage.",
    highlights: ["Cargo cover advisory", "Risk assessment", "Claims support"],
    overview: "We help clients protect cargo value with appropriate marine insurance solutions. From risk assessment and cover advisory to claims facilitation, our team ensures consignments are safeguarded throughout the journey.",
    process: [
      { step: "Risk Assessment", text: "Evaluation of cargo, route and exposure profile." },
      { step: "Cover Advisory", text: "Guidance on appropriate policy terms and conditions." },
      { step: "Policy Facilitation", text: "Coordination of certificates and documentation." },
      { step: "Claims Support", text: "Assistance through survey, documentation and settlement." },
    ],
    applications: ["All-Risk Cargo", "Project Cargo", "Bulk Shipments", "Multimodal"],
    benefits: [
      { title: "Value Protection", text: "Appropriate cover that safeguards consignment value." },
      { title: "Expert Advisory", text: "Clear guidance on terms, conditions and exclusions." },
      { title: "Claims Assurance", text: "Hands-on support through the claims process." },
    ],
  },
];

export const INDUSTRIES = [
  { slug: "maritime", name: "Maritime", icon: Ship, image: IMAGES.heroPrimary, text: "Comprehensive agency, chartering and port logistics support for shipping lines, owners and operators across global trade lanes." },
  { slug: "agriculture", name: "Agriculture", icon: Wheat, image: IMAGES.u_cargo, text: "Reliable handling of agricultural commodities and bulk produce with careful attention to seasonality, quality and timely movement." },
  { slug: "manufacturing", name: "Manufacturing", icon: Factory, image: IMAGES.u_port1, text: "Inbound and outbound logistics that keep production lines supplied and finished goods moving to market efficiently." },
  { slug: "fmcg", name: "FMCG", icon: ShoppingBasket, image: IMAGES.u_warehouse, text: "Fast, accurate distribution and fulfilment for high-velocity consumer goods with disciplined inventory governance." },
  { slug: "infrastructure", name: "Infrastructure", icon: Building2, image: IMAGES.aerialPort, text: "Project logistics and heavy-lift coordination supporting large-scale infrastructure and construction programmes." },
];

export const CERTIFICATIONS = [
  {
    abbr: "ISO 9000",
    icon: BadgeCheck,
    title: "Quality Management Systems",
    text: "Operations governed by internationally recognised quality management standards \u2014 ensuring consistency, accountability and continual improvement.",
  },
  {
    abbr: "FIATA",
    icon: Globe2,
    title: "International Federation of Freight Forwarders Associations",
    text: "Aligned with the global federation that sets the benchmark for professional freight forwarding practice and ethical standards worldwide.",
  },
  {
    abbr: "FFFAI",
    icon: Award,
    title: "Federation of Freight Forwarders' Associations in India",
    text: "An affiliation reflecting our standing within India's organised freight forwarding community and our commitment to industry best practice.",
  },
];

export const CONTACTS = {
  ceo: { role: "Chief Executive Officer", name: "Sib Sankar Maitra", phone: "+91 9831449754" },
  operations: { role: "Operations", name: "Rajib Das", phone: "+91 9831681202" },
  office: {
    role: "Head Office",
    lines: ["6th Floor, Nicco House", "2 Hare Street", "B.B.D. Bagh", "Kolkata \u2013 700001", "West Bengal, India"],
  },
};

export const COMPANY = {
  name: "Speed Shipping Agencies",
  short: "SSA",
  since: 2002,
  tagline: "Precision. Reliability. Global Reach.",
  email: "queries@speedshippingagencies.com",
  officePhone: "+91 9831449754",
  officePhoneTel: "+919831449754",
};

export { Compass };
